import { useState, useCallback, useEffect, useMemo } from 'react';
import { useTemplateWorkflow } from '@/hooks/useTemplateWorkflow';
import { useAssistant } from '@/hooks/useAssistant';
import { useSpecialty } from '@/contexts/SpecialtyContext';
import { useDemoMode } from '@/contexts/DemoModeContext';
import { useDenialRiskAnalysis, type DenialRiskResult } from '@/hooks/useDenialRiskAnalysis';
import { WorkflowStepIndicator } from '@/components/surgeon/v2/WorkflowStepIndicator';
import { PatientSelectionStep } from '@/components/surgeon/v2/PatientSelectionStep';
import { TemplateSelectionStep } from '@/components/surgeon/v2/TemplateSelectionStep';
import { CodingReviewStep } from '@/components/surgeon/v2/CodingReviewStep';
import { AuthorizationStep } from '@/components/surgeon/v2/AuthorizationStep';
import { DemoAgenticAssistant } from '@/components/surgeon/v2/demo-agentic/DemoAgenticAssistant';
import { TemplateWizardDialog } from '@/components/surgeon/v2/TemplateWizardDialog';
import { DenialDefenderOverlay, VulnerabilityGap } from '@/components/surgeon/v2/denial-defender/DenialDefenderOverlay';
import { DenialRiskAlertPanel } from '@/components/surgeon/v2/denial-defender/DenialRiskAlertPanel';
import { Button } from '@/components/ui/button';
import { ArrowLeft, ArrowRight, RotateCcw, Loader2, Shield, ClipboardList } from 'lucide-react';
import { buildVariableContext, renderTemplate } from '@/lib/templates/templateVariables';
import { evaluateOrthopedicCase } from '@/lib/denialDefenderRules';
import { buildPreAuthAuthoringAudit } from '@/lib/surgeon-console/preAuthAuthoringBrain';
import {
  createOfficeManagerFlag,
  saveOfficeManagerFlag,
} from '@/lib/surgeon-console/finalizedAuthorization';
import { isPerfectWorldDemo } from '@/lib/demo/perfectWorldDemo';
import { buildPreAuthDemoAgentExperience } from '@/lib/demo/demoAgenticExperience';
import { toast } from 'sonner';
import type { SurgeonTemplate, ScheduledCase, Laterality } from '@/lib/templates/types';

const cptCode = (line: { code: string | number }) => String(line.code);

const STEPS = [
  { label: 'Patient', shortLabel: 'Patient' },
  { label: 'Template & Laterality', shortLabel: 'Template' },
  { label: 'Code Cart', shortLabel: 'Code Cart' },
  { label: 'Authorization', shortLabel: 'Authorize' },
];

interface ClinicAuthWorkspaceProps {
  onDemoPreAuthSigned?: (caseId: string) => void;
}

export function ClinicAuthWorkspace({ onDemoPreAuthSigned }: ClinicAuthWorkspaceProps) {
  const workflow = useTemplateWorkflow();
  const { state } = workflow;
  const { selectedAssistant } = useAssistant();
  const { specialty } = useSpecialty();
  const { isDemoMode } = useDemoMode();
  const [showTemplateWizard, setShowTemplateWizard] = useState(false);
  const perfectWorldDemo = isPerfectWorldDemo(isDemoMode, state.selectedCase?.id);

  // Enforce Specialty Domain Isolation: filter surgeon_templates by active specialty
  useEffect(() => {
    workflow.setActiveSpecialty(specialty);
  }, [specialty, workflow]);

  // Denial Defender state
  const [showDefenderScan, setShowDefenderScan] = useState(false);
  const [defenderAnswers, setDefenderAnswers] = useState<Record<string, string>>({});

  // Denial Risk Analysis state
  const { isAnalyzing, result: denialRiskResult, analyze, clearResult } = useDenialRiskAnalysis();
  const [showRiskAlert, setShowRiskAlert] = useState(false);

  const getPayerName = () => {
    return state.selectedCase?.plan_name || state.selectedCase?.payer_name || 'the payer';
  };

  const extractDiagnosisFromFix = useCallback((text: string) => {
    const codeMatch = text.match(/\b([A-Z][0-9]{1,2}(?:\.[A-Z0-9]{1,4})?)\b/i);
    if (!codeMatch) return null;

    const normalizedCode = codeMatch[1].toUpperCase();
    const descriptionMatch = text.match(/\(([^)]+)\)/);

    return {
      code: normalizedCode,
      description: descriptionMatch?.[1]?.trim() || normalizedCode,
    };
  }, []);

  const extractDiagnosisRemoval = useCallback((text: string) => {
    const removalMatch = text.match(/delete\s+(?:ICD-10\s+)?([A-Z][0-9]{1,2}(?:\.[A-Z0-9]{1,4})?)|remove\s+(?:ICD-10\s+)?([A-Z][0-9]{1,2}(?:\.[A-Z0-9]{1,4})?)/i);
    const code = removalMatch?.[1] || removalMatch?.[2];
    return code ? code.toUpperCase() : null;
  }, []);

  const renderNoteForCurrentState = useCallback(() => {
    if (!state.selectedTemplate?.narrative_template) return '';
    const context = buildVariableContext({
      patient: state.selectedCase,
      template: state.selectedTemplate,
      laterality: state.laterality,
      facility: state.selectedFacility?.facility_name,
      cptLines: state.cptLines,
    });
    let note = renderTemplate(state.selectedTemplate.narrative_template, context);
    if (state.clinicalContext?.trim()) {
      note += `\n\nClinical Summary (Extracted from uploaded document): ${state.clinicalContext.trim()}`;
    }
    if (state.selectedCase?.patient_name?.toLowerCase().includes('robert williams')) {
      note += '\n\nClinical Summary: Conservative management has been exhausted. Surgical intervention is medically necessary.';
    }
    return note;
  }, [state]);

  const preAuthAuthoringAudit = useMemo(() => {
    if (!state.selectedCase || !state.selectedTemplate) return null;
    const docText = (state.selectedCase.pre_loaded_docs || [])
      .map(doc => [doc.type, doc.extracted_text].filter(Boolean).join(': '))
      .join('\n\n');
    return buildPreAuthAuthoringAudit({
      selectedCase: state.selectedCase,
      template: state.selectedTemplate,
      cptLines: state.cptLines,
      laterality: state.laterality,
      clinicalText: [state.clinicalContext, state.renderedNote, docText].filter(Boolean).join('\n\n'),
      clinicalSeverityData: state.clinicalSeverityData,
    });
  }, [
    state.selectedCase,
    state.selectedTemplate,
    state.cptLines,
    state.laterality,
    state.clinicalContext,
    state.renderedNote,
    state.clinicalSeverityData,
  ]);

  const preAuthAuthoringBlocked = preAuthAuthoringAudit?.status === 'blocked';
  const effectivePreAuthAuthoringBlocked = perfectWorldDemo ? false : preAuthAuthoringBlocked;

  const handleNext = useCallback(() => {
    if (state.step === 2 && state.selectedTemplate?.narrative_template) {
      workflow.updateNote(renderNoteForCurrentState());
    }

    // Intercept Step 3 → 4 transition with Denial Defender
    if (state.step === 3) {
      if (effectivePreAuthAuthoringBlocked) {
        toast.error('Pre-auth stopped before draft', {
          description: preAuthAuthoringAudit?.blockers[0]?.title || 'Resolve the authoring brain blockers before generating a payer packet.',
        });
        return;
      }
      workflow.updateNote(state.renderedNote);
      if (perfectWorldDemo) {
        workflow.goNext();
        return;
      }
      setShowDefenderScan(true);
      return;
    }

    workflow.goNext();
  }, [state, workflow, renderNoteForCurrentState, effectivePreAuthAuthoringBlocked, preAuthAuthoringAudit, perfectWorldDemo]);

  /** Fortify the note with defender answers, then run the RCM analysis */
  const fortifyNoteAndAnalyze = useCallback((answers: Record<string, string>) => {
    setDefenderAnswers(answers);

    // Weave answers into the narrative
    let note = state.renderedNote;
    const fortifyLines: string[] = [];

    Object.entries(answers).forEach(([gapId, value]) => {
      if (gapId === 'gap-pt-duration') {
        fortifyLines.push(`The patient completed ${value} of supervised physical therapy and concurrent NSAID management prior to surgical recommendation, meeting the payer-required conservative therapy duration threshold.`);
      } else if (gapId === 'gap-xray' && value === 'yes') {
        fortifyLines.push(`Weight-bearing anteroposterior radiographs confirm Kellgren-Lawrence Grade 3+ degenerative changes with bone-on-bone articulation, satisfying radiographic evidence requirements for surgical authorization.`);
      } else if (gapId === 'gap-xray' && value === 'no') {
        fortifyLines.push(`Note: Weight-bearing AP X-ray with bone-on-bone findings was not confirmed. Additional imaging may be required per payer guidelines.`);
      } else if (gapId === 'gap-injection') {
        if (value !== 'None administered') {
          fortifyLines.push(`Pre-surgical injection protocol was satisfied with administration of ${value} injection, which provided only temporary symptomatic relief, further supporting the medical necessity of surgical intervention.`);
        }
      }
    });

    if (fortifyLines.length > 0) {
      note += '\n\n--- Auto-Fortified by Denial Defender ---\n\n' + fortifyLines.join('\n\n');
    }

    workflow.updateNote(note);

    toast.success('Narrative Fortified', {
      description: `${fortifyLines.length} threshold${fortifyLines.length > 1 ? 's' : ''} woven in. Running RCM audit...`,
    });

    // Now run the denial risk analysis
    runDenialRiskAnalysis(answers);
  }, [state.renderedNote, workflow]);

  const handleScanComplete = useCallback((gaps: VulnerabilityGap[]) => {
    setShowDefenderScan(false);

    // Auto-collect all AI-resolved answers — pass them straight through.
    // Unresolved gaps flow to the RCM audit which surfaces them with actionable fixes.
    const autoAnswers: Record<string, string> = {};
    gaps.forEach(g => { if (g.aiResolvedValue) autoAnswers[g.id] = g.aiResolvedValue; });

    const resolvedCount = Object.keys(autoAnswers).length;
    const unresolvedCount = gaps.length - resolvedCount;

    if (gaps.length === 0) {
      toast.success('Denial Defender: No vulnerabilities found', {
        description: 'Narrative is bulletproof. Running RCM audit...',
      });
    } else if (resolvedCount > 0 && unresolvedCount === 0) {
      toast.success(`Denial Defender: ${resolvedCount} threshold${resolvedCount !== 1 ? 's' : ''} auto-verified`, {
        description: 'Running RCM audit...',
      });
    } else if (unresolvedCount > 0) {
      toast.info('Denial Defender scan complete', {
        description: `${resolvedCount} auto-verified, ${unresolvedCount} flagged — RCM audit will surface details.`,
      });
    }

    fortifyNoteAndAnalyze(autoAnswers);
  }, [fortifyNoteAndAnalyze]);

  /** Call the analyze-pre-auth-risk edge function */
  const runDenialRiskAnalysis = useCallback(async (answers: Record<string, string>) => {
    if (!state.selectedCase) return;

    // Build Denial Defender evaluation so the risk analyzer respects active exemptions
    const activeCptCodes = state.cptLines.map(cptCode);
    const ddEvaluation = evaluateOrthopedicCase(
      activeCptCodes,
      state.selectedCase.payer_name || 'Unknown Payer',
      state.clinicalSeverityData,
    );

    const result = await analyze({
      selectedCase: state.selectedCase,
      cptLines: state.cptLines,
      defenderAnswers: answers,
      clinicalNotes: state.renderedNote,
      procedureTarget: state.selectedTemplate?.template_name,
      diagnosisCodes: state.selectedTemplate?.diagnosis_codes || [],
      clinicalSeverityData: state.clinicalSeverityData,
      denialDefenderResults: {
        payerFlags: ddEvaluation.payerFlags,
        gateResults: ddEvaluation.gateResults,
        requiredPTWeeks: ddEvaluation.requiredPTWeeks,
        exemptionActive: ddEvaluation.exemptionActive,
      },
    });

    if (!result) {
      // Analysis failed — keep the packet held. A pre-auth should not be drafted when
      // the authoring brain cannot prove the code universe.
      toast.error('RCM Audit Failed', {
        description: 'Could not complete denial risk analysis. Authorization remains locked until the audit succeeds.',
      });
      return;
    }

    // Auto-apply any auto_fix_code issues
    const autoFixes = result.actionable_issues.filter(i => i.resolution_type === 'auto_fix_code' && i.proposed_fix_data);
    if (autoFixes.length > 0) {
      applyAutoFixes(autoFixes);
    }

    if (result.verdict === 'APPROVED') {
      toast.success('RCM Audit: APPROVED', {
        description: `Clinical packet cleared.${autoFixes.length > 0 ? ` ${autoFixes.length} correction(s) auto-applied.` : ''} Proceeding to authorization.`,
      });
      workflow.goNext();
    } else {
      // BLOCKED — show alert panel on Code Cart
      setShowRiskAlert(true);
      toast.error('RCM Audit: BLOCKED', {
        description: 'Critical issues detected. Review the alert panel and correct before proceeding.',
      });
    }
  }, [state, workflow, analyze]);

  /** Auto-apply code fixes from the RCM analysis. Only applies short modifier-shaped suggestions
   *  (e.g. "XS", "G0289") — long sentence-style proposed_fix_data is left to the surgeon to apply
   *  via the issue card buttons, since they aren't valid modifiers. */
  const applyAutoFixes = useCallback((fixes: Array<{ code: string | null; proposed_fix_data: string | null }>) => {
    const isModifierShaped = (s: string) => /^[A-Z0-9]{1,5}$/.test(s.trim());
    fixes.forEach(fix => {
      if (!fix.code || !fix.proposed_fix_data) return;
      if (!isModifierShaped(fix.proposed_fix_data)) return;
      const line = state.cptLines.find(l => cptCode(l) === fix.code);
      if (line && !line.modifiers.includes(fix.proposed_fix_data)) {
        workflow.toggleModifier(fix.code, fix.proposed_fix_data);
      }
    });
  }, [state.cptLines, workflow]);

  /** Handle applying a single code fix from the alert panel */
  const handleApplyCodeFix = useCallback((issue: import('@/hooks/useDenialRiskAnalysis').ActionableIssue) => {
    if (issue.code && issue.proposed_fix_data) {
      const isModifierShaped = /^[A-Z0-9]{1,5}$/.test(issue.proposed_fix_data.trim());
      const line = state.cptLines.find(l => cptCode(l) === issue.code);
      if (isModifierShaped && line && !line.modifiers.includes(issue.proposed_fix_data)) {
        workflow.toggleModifier(issue.code, issue.proposed_fix_data);
        toast.success(`Applied: ${issue.proposed_fix_data} to CPT ${issue.code}`);
      } else if (!isModifierShaped) {
        // Long-form correction — append to the rendered note instead of mangling modifiers
        const marker = `RCM Audit Correction (${issue.code}): ${issue.proposed_fix_data}`;
        if (!state.renderedNote.includes(marker)) {
          workflow.updateNote(`${state.renderedNote.trim()}\n\n${marker}`);
        }
        toast.success('Correction noted in the case narrative');
      }
      // When G0289 is applied, inject supporting language for separate-compartment chondroplasty
      if (issue.proposed_fix_data.includes('G0289')) {
        const g0289Language = '\n\nHCPCS G0289 — Separate Compartment Chondroplasty: Arthroscopic chondroplasty was performed in a distinct, separate compartment from the primary arthroscopic procedure. Per CMS NCCI guidelines, G0289 is separately reportable when chondroplasty is performed in a separate compartment of the knee during the same operative session. The anatomical separation establishes independent medical necessity for this procedure and supports separate reimbursement.';
        if (!state.renderedNote.includes('G0289 — Separate Compartment')) {
          workflow.updateNote(state.renderedNote + g0289Language);
        }
      }
    }
  }, [state.cptLines, state.renderedNote, workflow]);

  /** Delete a CPT code entirely — used for the "same compartment" option on bundled codes */
  const handleDeleteCode = useCallback((code: string) => {
    workflow.removeCptLine(code);
    toast.success(`CPT ${code} removed from authorization request`);
  }, [workflow]);

  /** Route unresolved critical pre-auth items to staff so the surgeon can keep moving. */
  const handleProceedWithPendingItems = useCallback((issues: import('@/hooks/useDenialRiskAnalysis').ActionableIssue[]) => {
    if (state.selectedCase) {
      issues.forEach((issue) => {
        saveOfficeManagerFlag(createOfficeManagerFlag({
          caseId: state.selectedCase!.id,
          patientName: state.selectedCase!.patient_name,
          title: issue.description,
          detail: issue.bypass_reasoning || issue.detailed_fix_explanation || issue.proposed_fix_data || issue.description,
          affectedCodes: issue.code ? [issue.code] : [],
          source: 'pre-auth',
          severity: 'staff_hold',
        }));
      });
    }
    toast.info('Routed to office manager', {
      description: `${issues.length} critical item${issues.length !== 1 ? 's are' : ' is'} on staff hold. The surgeon can move to the next case.`,
    });
    workflow.reset();
  }, [state.selectedCase, workflow]);

  /** Per-issue flag — record a single item for RCM follow-up without clearing the alert. */
  const handleFlagAndMoveOn = useCallback((issue: import('@/hooks/useDenialRiskAnalysis').ActionableIssue) => {
    if (state.selectedCase) {
      saveOfficeManagerFlag(createOfficeManagerFlag({
        caseId: state.selectedCase.id,
        patientName: state.selectedCase.patient_name,
        title: issue.description,
        detail: issue.bypass_reasoning || issue.detailed_fix_explanation || issue.proposed_fix_data || issue.description,
        affectedCodes: issue.code ? [issue.code] : [],
        source: 'pre-auth',
        severity: issue.severity === 'critical' ? 'staff_hold' : 'rcm_review',
      }));
    }
    const marker = `[FLAGGED FOR RCM] ${issue.category}${issue.code ? ' ' + issue.code : ''}: ${issue.description}`;
    if (!state.renderedNote.includes(marker)) {
      const appended = `${state.renderedNote.trim()}\n\n${marker}${issue.bypass_reasoning ? '\n  → ' + issue.bypass_reasoning : ''}`;
      workflow.updateNote(appended);
    }
    toast.info('Item flagged for RCM', {
      description: 'Office manager will address this before submission.',
    });
  }, [state.renderedNote, state.selectedCase, workflow]);

  const handleApplySuggestedFix = useCallback((issue: import('@/hooks/useDenialRiskAnalysis').ActionableIssue) => {
    if (!issue.proposed_fix_data) return;

    const diagnosisToRemove = extractDiagnosisRemoval(issue.proposed_fix_data);
    if (diagnosisToRemove) {
      workflow.removeDiagnosisCode(diagnosisToRemove);
    }

    const noteAlreadyIncludesFix = state.renderedNote.toLowerCase().includes(issue.proposed_fix_data.toLowerCase());
    if (!noteAlreadyIncludesFix) {
      const nextNote = state.renderedNote.trim()
        ? `${state.renderedNote.trim()}\n\nRCM Audit Accepted Correction:\n${issue.proposed_fix_data}`
        : issue.proposed_fix_data;
      workflow.updateNote(nextNote);
    }

    const extractedDiagnosis = extractDiagnosisFromFix(issue.proposed_fix_data);
    if (extractedDiagnosis) {
      workflow.addDiagnosisCode(extractedDiagnosis);
    }

    toast.success('Accepted correction applied to the case narrative');
  }, [extractDiagnosisFromFix, extractDiagnosisRemoval, state.renderedNote, workflow]);

  const handleDismissRiskAlert = useCallback(() => {
    setShowRiskAlert(false);
    clearResult();
  }, [clearResult]);

  const handleReset = useCallback(() => {
    workflow.reset();
    setShowRiskAlert(false);
    clearResult();
  }, [workflow, clearResult]);

  const routeAuthoringBlockersToOfficeManager = useCallback(() => {
    if (!state.selectedCase || !preAuthAuthoringAudit) return;

    preAuthAuthoringAudit.blockers.forEach((item) => {
      saveOfficeManagerFlag(createOfficeManagerFlag({
        caseId: state.selectedCase!.id,
        patientName: state.selectedCase!.patient_name,
        title: item.title,
        detail: `${item.finding} ${item.prevention}`,
        affectedCodes: item.affectedCodes,
        source: 'pre-auth',
        severity: 'staff_hold',
      }));
    });

    toast.info('Flagged for office manager', {
      description: 'This case is on staff hold. The surgeon can keep moving to the next patient.',
    });
    workflow.reset();
  }, [preAuthAuthoringAudit, state.selectedCase, workflow]);

  const resolveSlapBicepsConflict = useCallback((keepCode: string, removeCode: string) => {
    workflow.resolveCptConflict({ keepCode, removeCode });
    toast.success(`Resolved coding conflict`, {
      description: `Kept CPT ${keepCode} and removed CPT ${removeCode} for this authorization.`,
    });
  }, [workflow]);

  const handleStepClick = useCallback((step: number) => {
    if (step === state.step) return;
    if (step >= 3 && state.step === 2 && state.selectedTemplate?.narrative_template) {
      workflow.updateNote(renderNoteForCurrentState());
    }
    if (step === 4 && state.step === 3) {
      if (effectivePreAuthAuthoringBlocked) {
        toast.error('Pre-auth stopped before draft', {
          description: preAuthAuthoringAudit?.blockers[0]?.title || 'Resolve the authoring brain blockers before generating a payer packet.',
        });
        return;
      }
      workflow.updateNote(state.renderedNote);
      if (perfectWorldDemo) {
        workflow.setStep(step);
        return;
      }
      setShowDefenderScan(true);
      return;
    }
    workflow.setStep(step);
  }, [state, workflow, renderNoteForCurrentState, effectivePreAuthAuthoringBlocked, preAuthAuthoringAudit, perfectWorldDemo]);

  const canNavigateToStep = useCallback((step: number) => {
    if (step === 1) return true;
    return !!state.selectedCase;
  }, [state.selectedCase]);

  const effectiveStep = Math.min(state.step, 4);
  const demoAgentExperience = useMemo(() => {
    if (!perfectWorldDemo || !state.selectedCase || effectiveStep < 2) return null;

    return buildPreAuthDemoAgentExperience({
      step: effectiveStep,
      patientName: state.selectedCase.patient_name,
      procedureName: state.selectedTemplate?.template_name
        || state.selectedCase.scheduled_procedure
        || 'Selected procedure',
      cptCodes: state.cptLines.map(cptCode),
      payerName: state.selectedCase.plan_name || state.selectedCase.payer_name || 'Simulated payer',
      documents: state.selectedCase.pre_loaded_docs,
    });
  }, [
    effectiveStep,
    perfectWorldDemo,
    state.cptLines,
    state.selectedCase,
    state.selectedTemplate?.template_name,
  ]);

  return (
    <div className="space-y-5">
      <WorkflowStepIndicator
        currentStep={effectiveStep}
        steps={STEPS}
        onStepClick={handleStepClick}
        canNavigateToStep={canNavigateToStep}
      />

      {demoAgentExperience && (
        <DemoAgenticAssistant experience={demoAgentExperience} />
      )}

      {/* Denial Risk Alert Panel — shown on Code Cart when BLOCKED */}
      {effectiveStep === 3 && !perfectWorldDemo && showRiskAlert && denialRiskResult && (
        <DenialRiskAlertPanel
          result={denialRiskResult}
          onDismiss={handleDismissRiskAlert}
          onApplyCodeFix={handleApplyCodeFix}
          onApplySuggestedFix={handleApplySuggestedFix}
          onDeleteCode={handleDeleteCode}
          onProceedWithPendingItems={handleProceedWithPendingItems}
          onFlagAndMoveOn={handleFlagAndMoveOn}
          mode="pre-auth"
        />
      )}

      <div className="min-h-[50vh]">
        {effectiveStep === 1 && (
          <PatientSelectionStep
            searchResults={state.searchResults}
            selectedCase={state.selectedCase}
            isSearching={state.isSearching}
            onSearch={workflow.searchPatients}
            onSelect={(c: ScheduledCase, t?: SurgeonTemplate, lat?: Laterality | null, ctx?: string, sevData?: any) => workflow.selectPatient(c, t, lat ?? undefined, ctx, sevData)}
          />
        )}

        {effectiveStep === 2 && state.selectedCase && (
          <TemplateSelectionStep
            selectedCase={state.selectedCase}
            templates={state.templates}
            selectedTemplate={state.selectedTemplate}
            laterality={state.laterality}
            facilities={state.facilities}
            selectedFacility={state.selectedFacility}
            onSelectTemplate={workflow.selectTemplate}
            onSetLaterality={workflow.setLaterality}
            onSetFacility={workflow.setFacility}
            onOpenTemplateWizard={() => setShowTemplateWizard(true)}
            onChangePatient={() => workflow.setStep(1)}
            specialty={specialty}
            clinicalSeverityData={state.clinicalSeverityData}
          />
        )}

        {effectiveStep === 3 && state.selectedCase && state.selectedTemplate && (
          <div className="space-y-4">
            {!perfectWorldDemo && preAuthAuthoringBlocked && preAuthAuthoringAudit && (
              <div className="rounded-xl border-2 border-destructive/50 bg-destructive/5 p-4 space-y-3">
                <div className="flex items-start gap-3">
                  <Shield className="w-5 h-5 text-destructive mt-0.5 shrink-0" />
                  <div className="min-w-0">
                    <h3 className="text-sm font-bold text-destructive">Authoring Brain stopped the draft</h3>
                    <p className="text-xs text-muted-foreground mt-1">
                      Choose the surgeon-intended path now, or flag the case for staff and keep moving.
                    </p>
                  </div>
                </div>

                {preAuthAuthoringAudit.blockers.map((item) => (
                  <div key={item.id} className="rounded-lg border border-border bg-background p-3">
                    <p className="text-sm font-semibold text-foreground">{item.title}</p>
                    <p className="text-xs text-muted-foreground mt-1">{item.finding}</p>
                    {item.id === 'shoulder-slap-biceps-overlap' && (
                      <div className="mt-3 flex flex-wrap gap-2">
                        <Button
                          size="sm"
                          onClick={() => resolveSlapBicepsConflict('29807', '23430')}
                        >
                          Keep SLAP Repair
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => resolveSlapBicepsConflict('23430', '29807')}
                        >
                          Keep Biceps Tenodesis
                        </Button>
                        <Button
                          size="sm"
                          variant="secondary"
                          className="gap-2"
                          onClick={routeAuthoringBlockersToOfficeManager}
                        >
                          <ClipboardList className="w-3.5 h-3.5" />
                          Flag for Office Manager
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            <CodingReviewStep
              selectedCase={state.selectedCase}
              template={state.selectedTemplate}
              laterality={state.laterality}
              facility={state.selectedFacility}
              cptLines={state.cptLines}
              renderedNote={state.renderedNote}
              assistantNoteEnabled={state.assistantNoteEnabled}
              selectedAssistant={selectedAssistant}
              onRemoveLine={workflow.removeCptLine}
              onAddLine={workflow.addCptLine}
              onToggleModifier={workflow.toggleModifier}
              onRemoveAssistantNote={workflow.removeAssistantNote}
              onTemplateUpdated={workflow.reloadTemplates}
              caseId={state.selectedCase.id}
              payerPlanId={state.selectedCase.plan_name || null}
              highComplexity={state.highComplexity}
              onHighComplexityChange={workflow.setHighComplexity}
              bmiValue={state.bmiValue}
              onBmiValueChange={workflow.setBmiValue}
              clinicalSeverityData={state.clinicalSeverityData}
            />
          </div>
        )}

        {effectiveStep === 4 && state.selectedCase && state.selectedTemplate && (
          <AuthorizationStep
            selectedCase={state.selectedCase}
            template={state.selectedTemplate}
            laterality={state.laterality}
            facility={state.selectedFacility}
            cptLines={state.cptLines}
            renderedNote={state.renderedNote}
            isFinalized={state.isFinalized}
            onFinalize={workflow.finalize}
            highComplexity={state.highComplexity}
            onHighComplexityChange={workflow.setHighComplexity}
            bmiValue={state.bmiValue}
            onBmiValueChange={workflow.setBmiValue}
            assistantNoteEnabled={state.assistantNoteEnabled}
            selectedAssistant={selectedAssistant}
            clinicalContext={state.clinicalContext}
            clinicalSeverityData={state.clinicalSeverityData}
            onDemoPreAuthSigned={onDemoPreAuthSigned}
          />
        )}
      </div>

      <div className="flex items-center justify-between pt-4 border-t">
        <div>
          {effectiveStep > 1 ? (
            <Button variant="outline" onClick={workflow.goBack}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
          ) : (
            <Button variant="ghost" onClick={handleReset}>
              <RotateCcw className="w-4 h-4 mr-2" />
              Reset
            </Button>
          )}
        </div>
        {effectiveStep === 2 && (
          <Button onClick={handleNext} disabled={!workflow.canProceed} className="gap-2">
            Code Cart
            <ArrowRight className="w-4 h-4" />
          </Button>
        )}
        {effectiveStep >= 3 && effectiveStep < 4 && (
          <div className="flex flex-wrap justify-end gap-2">
            {effectivePreAuthAuthoringBlocked && (
              <Button
                variant="outline"
                onClick={routeAuthoringBlockersToOfficeManager}
                className="gap-2 border-amber-300 text-amber-800 hover:bg-amber-50"
              >
                <ClipboardList className="w-4 h-4" />
                Flag for Office Manager
              </Button>
            )}
            <Button
              onClick={handleNext}
              disabled={!workflow.canProceed || isAnalyzing || effectivePreAuthAuthoringBlocked}
              className="gap-2 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-6"
            >
              {isAnalyzing ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Denial Defender: Auditing...
                </>
              ) : (
                <>
                  {effectivePreAuthAuthoringBlocked ? (
                    <>
                      Blocked by Authoring Brain
                      <Shield className="w-4 h-4" />
                    </>
                  ) : (
                    <>
                      Generate Pre-Auth
                      <ArrowRight className="w-4 h-4" />
                    </>
                  )}
                </>
              )}
            </Button>
          </div>
        )}
      </div>

      <TemplateWizardDialog
        open={showTemplateWizard}
        onOpenChange={setShowTemplateWizard}
        onCreated={() => workflow.reset()}
      />

      <DenialDefenderOverlay
        show={showDefenderScan}
        payerName={getPayerName()}
        clinicalSeverityData={state.clinicalSeverityData}
        activeCptCodes={state.cptLines.map(cptCode)}
        onScanComplete={handleScanComplete}
      />


      {/* Full-screen analyzing overlay */}
      {isAnalyzing && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-background/95 backdrop-blur-md">
          <div className="flex flex-col items-center gap-6 max-w-lg text-center px-6">
            <div className="relative">
              <div className="w-24 h-24 rounded-full bg-primary/10 flex items-center justify-center">
                <Shield className="w-12 h-12 text-primary animate-pulse" />
              </div>
              <Loader2 className="absolute -top-2 -left-2 w-28 h-28 text-primary/30 animate-spin" />
            </div>
            <div className="space-y-2">
              <h2 className="text-xl font-bold text-foreground tracking-tight">
                Denial Defender: RCM Audit in Progress
              </h2>
              <p className="text-sm text-primary font-medium">
                Auditing Payer Guidelines & Coding Edits…
              </p>
            </div>
            <p className="text-sm text-muted-foreground">
              Cross-referencing CPT modifiers against NCCI bundling rules, verifying MCG/InterQual biomarker thresholds, and scanning for conservative therapy traps…
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
